import { Breakpoint, SxProps, Theme, styled } from "@mui/material/styles";
import { ReactElement, ReactNode, useMemo } from "react";

import CheckIcon from "@mui/icons-material/Check";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import StyledDialogTitle from "./StyledDialogTitle";
import StyledLoadingButton from "../../providers/theme/design-tokens/LoadingButton/StyledLoadingButton";

interface IStyledDialog {
  open: boolean;
  title?: string;
  subTitle?: string;
  onClose: () => void;
  sx?: SxProps<Theme>;
  fullScreen?: boolean;
  maxWidth?: Breakpoint | false;
  content?: ReactNode;
  children?: ReactNode;
  showSubmitButton?: boolean;
  disableEscapeKeyDown?: boolean;
  SubmitButtonProps?: {
    loading?: boolean;
    title?: string;
    icon?: ReactElement | JSX.Element;
    sx?: SxProps<Theme>;
    onSubmit?: () => void;
  };
}

const DialogWindow = styled(Dialog)(
  ({
    theme,
  }): {
    "& .MuiDialog-paper": {
      padding: string | number;
      borderRadius: string | number;
    };
    "& .MuiDialogContent-root": {
      padding: string | number;
      overflowY: string;
    };
    "& .MuiDialogActions-root": {
      padding: string | number;
    };
  } => ({
    "& .MuiDialog-paper": {
      padding: "48px 64px",
      borderRadius: "12px",
    },
    "& .MuiDialogContent-root": {
      padding: theme.spacing(0),
      overflowY: "initial",
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(3, 0, 0, 0),
    },
  })
);

const StyledDialog = ({
  open,
  onClose,
  title,
  subTitle,
  content,
  children,
  SubmitButtonProps,
  disableEscapeKeyDown,
  fullScreen = false,
  showSubmitButton = true,
  maxWidth = "sm",
  sx,
}: IStyledDialog): JSX.Element => {
  const loading = SubmitButtonProps?.loading ?? false;
  const icon = SubmitButtonProps?.icon;
  const buttonTitle = SubmitButtonProps?.title ?? "";
  const onSubmit = SubmitButtonProps?.onSubmit;
  const contentSx = SubmitButtonProps?.sx ?? {};

  const memoizedContent = useMemo(
    (): ReactNode => content ?? children,
    [content, children]
  );

  return (
    <DialogWindow
      onClose={onClose}
      open={open}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      fullWidth
      scroll="body"
      disableEscapeKeyDown={disableEscapeKeyDown}
      sx={sx}
    >
      {title && (
        <StyledDialogTitle
          onClose={onClose}
          title={title}
          subTitle={subTitle}
        />
      )}

      <DialogContent
        sx={{
          height: "100%",
        }}
      >
        {memoizedContent}
      </DialogContent>

      {showSubmitButton && (
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <StyledLoadingButton
            fullWidth
            loading={loading}
            loadingPosition="start"
            startIcon={icon ?? <CheckIcon />}
            variant="contained"
            color="primary"
            onClick={onSubmit}
            sx={contentSx}
          >
            {buttonTitle ?? "Create"}
          </StyledLoadingButton>
        </DialogActions>
      )}
    </DialogWindow>
  );
};

export default StyledDialog;
